import Box from '@mui/material/Box';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MFSubmitButton } from '../../lib/formik';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { passwordRegex } from '../../utils/regex';
import { LoginLayout } from '../commonComponents';
import { FormikPasswordField } from '../../lib/formik/TextField';
import { resetPassword } from '../../redux-store/actions/userManagement';
import { ResetPasswordSuccessResponse } from '../../redux-store/types/user';
import { useSnackbar } from 'notistack';

interface Values {
  password: string;
  confirmPassword: string;
}

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .nullable()
    .required('Enter new password')
    .matches(
      passwordRegex,
      'Password must be at least 8 characters long and must contain at least one uppercase letter, one lowercase letter, one numeral, and one special character'
    ),
  confirmPassword: yup
    .string()
    .nullable()
    .required('Re-enter new password')
    .test('confirmPassword', (value, context) => {
      const { path, createError, parent } = context;
      if (parent.password && value && parent.password !== value) {
        return createError({
          message: `Passwords don't match`,
          path,
        });
      }
      return true;
    }),
});

export default function ChangePassword(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (values: Values) => {
    try {
      const { password = '', confirmPassword = '' } = values;
      const res = (await dispatch(
        resetPassword({
          password,
          confirmPassword,
        })
      )) as unknown as ResetPasswordSuccessResponse;
      enqueueSnackbar(res.message, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      history.push('/login');
    } catch (e) {
      console.error((e as unknown as Error).message);
    }
  };
  const initialValues: Values = {
    password: '',
    confirmPassword: '',
  };

  return (
    <LoginLayout LoginText="Change Password">
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
        {({ handleSubmit }) => (
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              '.MuiLoadingButton-root': {
                mt: 5,
              },
              '.MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: 16,
                fontWeight: 500,
              },
            }}>
            <FormikPasswordField
              label="New Password"
              name="password"
              placeholder="Enter New Password"
            />
            <FormikPasswordField
              label="Re-Enter New Password"
              name="confirmPassword"
              placeholder="Re-Enter New Password"
            />
            <MFSubmitButton label="Next" />
          </Box>
        )}
      </Formik>
    </LoginLayout>
  );
}
